import { useEffect, useRef, useState } from 'react'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import { Button, Input, Modal, Spin } from 'antd'
import {
  CloseOutlined,
  CopyOutlined,
  ReloadOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { MAX_REGENERATION_INSTRUCTIONS_LENGTH } from '../../lib/shared/cleoRegenerate'
import { MarkdownText } from '../MarkdownText'
import styles from './ChatwootInbox.module.less'

interface ChatwootCleoCardProps {
  // Only the two card-worthy states render here; idle/error/noReply are handled
  // by the composer (they show the reply field instead).
  status: 'generating' | 'ready'
  suggestion: string | null
  // An outbound send (Accept & send) is in flight.
  sending: boolean
  // Animation class applied while the card plays its exit before the composer
  // swaps in the reply field (accept lifts up, copy drops down, reject fades).
  exitClass?: string
  // The card is mid-exit; controls are disabled so a second click can't fire.
  exiting: boolean
  onAccept: () => void
  onCopy: () => void
  onReject: () => void
  onRegenerate: (instructions?: string) => void
}

/**
 * The Cleo reply-draft card body, rendered inside the composer box in
 * place of the reply field. Shows the drafting spinner or the ready draft with
 * its decision controls (Reject · Regenerate · Copy to editor · Accept & send),
 * and owns the regenerate modal (instruction chips + free text). Purely
 * presentational — all lifecycle lives in useChatwootCleoSuggestion.
 */
export function ChatwootCleoCard({
  status,
  suggestion,
  sending,
  exitClass,
  exiting,
  onAccept,
  onCopy,
  onReject,
  onRegenerate,
}: ChatwootCleoCardProps) {
  const { t } = useTranslation()
  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [instructions, setInstructions] = useState('')
  const controlsDisabled = sending || exiting

  const cardClassName = [styles.cleoCard, exitClass].filter(Boolean).join(' ')

  if (status === 'generating') {
    return (
      <div className={cardClassName}>
        <div className={styles.cleoCardLoading}>
          <Spin size="small" />
          <span>{t('frontend:chatwoot.cleo.drafting')}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={cardClassName}>
      {/* Render Markdown so the operator previews the exact formatting the
          customer will see; the draft box scrolls internally so a long draft
          never grows the composer on its own. The composer measures this box
          (data-composer-scroll) to offer its expand toggle when the draft
          does not fit (CL-2886). */}
      <div className={styles.cleoDraft} data-composer-scroll="">
        <MarkdownText value={suggestion ?? ''} />
      </div>
      <div className={styles.cleoActions}>
        <Button
          icon={<CloseOutlined />}
          disabled={controlsDisabled}
          onClick={onReject}
        >
          {t('frontend:chatwoot.cleo.reject')}
        </Button>
        <div className={styles.cleoActionsPrimary}>
          <Button
            icon={<ReloadOutlined />}
            disabled={controlsDisabled}
            onClick={() => setRegenerateOpen(true)}
          >
            {t('frontend:chatwoot.cleo.regenerate')}
          </Button>
          <Button
            icon={<CopyOutlined />}
            disabled={controlsDisabled}
            onClick={onCopy}
          >
            {t('frontend:chatwoot.cleo.copyToEditor')}
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            disabled={exiting}
            onClick={onAccept}
          >
            {t('frontend:chatwoot.cleo.acceptSend')}
          </Button>
        </div>
      </div>

      <RegenerateModal
        open={regenerateOpen}
        onCancel={() => setRegenerateOpen(false)}
        onConfirm={(text) => {
          setRegenerateOpen(false)
          onRegenerate(text || undefined)
        }}
        instructions={instructions}
        setInstructions={setInstructions}
      />
    </div>
  )
}

// Regenerate dialog: quick instruction chips plus a free-text field. An empty
// submission asks Cleo for a fresh attempt; text refines the current draft.
function RegenerateModal({
  open,
  onCancel,
  onConfirm,
  instructions,
  setInstructions,
}: {
  open: boolean
  onCancel: () => void
  onConfirm: (instructions: string) => void
  instructions: string
  setInstructions: (value: string) => void
}) {
  const { t } = useTranslation()
  const textAreaRef = useRef<TextAreaRef>(null)

  useEffect(() => {
    if (open) {
      setInstructions('')
      const timer = window.setTimeout(() => textAreaRef.current?.focus(), 50)
      return () => window.clearTimeout(timer)
    }
  }, [open, setInstructions])

  // Static keys (no template-literal t()) so the i18n key checker can verify them.
  const chips: { key: string; label: string; instruction: string }[] = [
    {
      key: 'shorter',
      label: t('frontend:chatwoot.cleo.regenChipShorter'),
      instruction: t('frontend:chatwoot.cleo.regenChipShorterInstruction'),
    },
    {
      key: 'formal',
      label: t('frontend:chatwoot.cleo.regenChipFormal'),
      instruction: t('frontend:chatwoot.cleo.regenChipFormalInstruction'),
    },
    {
      key: 'detail',
      label: t('frontend:chatwoot.cleo.regenChipDetail'),
      instruction: t('frontend:chatwoot.cleo.regenChipDetailInstruction'),
    },
  ]

  return (
    <Modal
      title={t('frontend:chatwoot.cleo.regenerateTitle')}
      open={open}
      okText={t('frontend:chatwoot.cleo.regenerateConfirm')}
      cancelText={t('frontend:chatwoot.cleo.regenerateCancel')}
      onOk={() => onConfirm(instructions.trim())}
      onCancel={onCancel}
      destroyOnClose
    >
      <p style={{ marginTop: 0 }}>
        {t('frontend:chatwoot.cleo.regenerateHint')}
      </p>
      <div
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}
      >
        {chips.map((chip) => (
          <Button
            key={chip.key}
            shape="round"
            size="small"
            onClick={() => setInstructions(chip.instruction)}
          >
            {chip.label}
          </Button>
        ))}
      </div>
      <Input.TextArea
        ref={textAreaRef}
        value={instructions}
        onChange={(event) => setInstructions(event.target.value)}
        placeholder={t('frontend:chatwoot.cleo.regeneratePlaceholder')}
        maxLength={MAX_REGENERATION_INSTRUCTIONS_LENGTH}
        showCount
        autoSize={{ minRows: 3, maxRows: 6 }}
      />
    </Modal>
  )
}
